import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import './PricingWebComponent';

interface PricingWebComponentElement extends HTMLElement {
  context: 'marketing' | 'authenticated';
  currency: 'USD' | 'EUR';
  interval: 'monthly' | 'annually';
  translations: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class PricingWebComponentService {
  
  constructor(private translate: TranslateService) {}

  /**
   * Create and configure a pricing web component
   */
  createPricingComponent(config: {
    context: 'marketing' | 'authenticated';
    currency?: 'USD' | 'EUR';
    interval?: 'monthly' | 'annually';
    userPlan?: number;
    userInterval?: 'monthly' | 'annually';
    showToggles?: boolean;
    showSavings?: boolean;
    theme?: 'dark' | 'light';
  }): PricingWebComponentElement {
    const pricingComponent = document.createElement('pricing-component') as PricingWebComponentElement;
    
    // Set properties
    pricingComponent.context = config.context;
    pricingComponent.currency = config.currency || 'USD';
    pricingComponent.interval = config.interval || 'monthly';
    pricingComponent.translations = this.getTranslations();
    
    // Set attributes
    if (config.userPlan !== undefined) {
      pricingComponent.setAttribute('user-plan', config.userPlan.toString());
    }
    if (config.userInterval) {
      pricingComponent.setAttribute('user-interval', config.userInterval);
    }
    if (config.showToggles !== undefined) {
      pricingComponent.setAttribute('show-toggles', config.showToggles.toString());
    }
    if (config.showSavings !== undefined) {
      pricingComponent.setAttribute('show-savings', config.showSavings.toString());
    }
    if (config.theme) {
      pricingComponent.setAttribute('theme', config.theme);
    }
    
    return pricingComponent;
  }

  /**
   * Get translations for the pricing component
   */
  private getTranslations(): Record<string, string> {
    return {
      PRICING_SECTION_TITLE: this.translate.instant('PRICING_SECTION_TITLE') || 'Choose the right plan for you',
      PRICING_SECTION_SUBTITLE: this.translate.instant('PRICING_SECTION_SUBTITLE') || 'Start free and upgrade as your poker games grow',
      PRICING_SAVINGS_MESSAGE: this.translate.instant('PRICING_SAVINGS_MESSAGE') || 'Save {amount} vs monthly',
      MONTHLY_BILLING: this.translate.instant('MONTHLY_BILLING') || 'Monthly',
      ANNUAL_BILLING: this.translate.instant('ANNUAL_BILLING') || 'Annual',
      SIGNUP: this.translate.instant('SIGNUP') || 'Sign Up',
      YOUR_PLAN: this.translate.instant('YOUR_PLAN') || 'Your Plan',
      UPGRADE: this.translate.instant('UPGRADE') || 'Upgrade',
      DOWNGRADE: this.translate.instant('DOWNGRADE') || 'Downgrade',
      SELECT: this.translate.instant('SELECT') || 'Select',
      FREE: this.translate.instant('FREE') || 'Free',
      ADMIN_CONTACT_US: this.translate.instant('ADMIN_CONTACT_US') || 'Contact Us',
      LEAGUES: this.translate.instant('LEAGUES') || 'Leagues',
      FREE_PLAN_DESCRIPTION: this.translate.instant('FREE_PLAN_DESCRIPTION') || 'Perfect for casual home games with friends',
      ADMIN_EVERYTHING_IN_KITCHEN_TABLE: this.translate.instant('ADMIN_EVERYTHING_IN_KITCHEN_TABLE') || 'Everything from Kitchen Table plan plus:',
      ADMIN_EVERYTHING_HOME_GAME: this.translate.instant('ADMIN_EVERYTHING_HOME_GAME') || 'Everything from the Home Game plan plus:',
      MAX_9: this.translate.instant('MAX_9') || 'Max 9 players',
      MAX_27: this.translate.instant('MAX_27') || 'Max 27 players',
      ADMIN_REAL_TIME: this.translate.instant('ADMIN_REAL_TIME') || 'Real time synchronization',
      ADMIN_BLIND_STRUCTURE_CALCULATOR: this.translate.instant('ADMIN_BLIND_STRUCTURE_CALCULATOR') || 'Blind structure calculator',
      TOURNAMENT_CLOCK: this.translate.instant('TOURNAMENT_CLOCK') || 'Tournament clock',
      ADMIN_PAYOUT_CALCULATOR: this.translate.instant('ADMIN_PAYOUT_CALCULATOR') || 'Payout calculator',
      ADMIN_SEATING: this.translate.instant('ADMIN_SEATING') || 'Seating management',
      ADMIN_AD_FREE: this.translate.instant('ADMIN_AD_FREE') || 'Ad free',
      ADMIN_UNLIMITED_PLAYERS: this.translate.instant('ADMIN_UNLIMITED_PLAYERS') || 'Unlimited players',
      ADMIN_BOUNTY_TOURNAMENTS: this.translate.instant('ADMIN_BOUNTY_TOURNAMENTS') || 'Bounty tournaments',
      ADMIN_CUSTOM_BACKGROUND: this.translate.instant('ADMIN_CUSTOM_BACKGROUND') || 'Custom background',
      ADMIN_YOUR_LOGO: this.translate.instant('ADMIN_YOUR_LOGO') || 'Your logo',
      ENHANCED_CHIPSET_MANAGEMENT: this.translate.instant('ENHANCED_CHIPSET_MANAGEMENT') || 'Enhanced chipset management',
      ADMIN_CREATE_LEAGUES: this.translate.instant('ADMIN_CREATE_LEAGUES') || 'Create leagues',
      ADMIN_DEFINE_LEAGUE_RULES: this.translate.instant('ADMIN_DEFINE_LEAGUE_RULES') || 'Define league rules',
      ADMIN_AUTOMATIC_LEAGUE_RANKINGS: this.translate.instant('ADMIN_AUTOMATIC_LEAGUE_RANKINGS') || 'Automatic league rankings',
    };
  }
}
